import React, { useMemo } from "react";
import { Card, Col, Row, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { usePermissionRequirement } from "~/hooks/usePermission";
import CollectionsDashboard from "~/modules/Collections/CollectionsDashboard";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import DiscountsList from "~/modules/Products/Discounts/DiscountsList";
import { ProductsCustomers } from "~/modules/Products/ProductsCustomers/ProductsCustomers";
import ProductsList from "~/modules/Products/ProductsList/ProductsList";
import { ProductsTransactions } from "~/modules/Products/ProductsTransactions/ProductsTransactions";
import ProductsSettings from "./ProductsSettings/ProductsSettings";

const Products = (): React.ReactElement => {
  const { t } = useTranslation("products");
  const [searchParams, setSearchParams] = useSearchParams();
  // Lustro RLS, nie zgadywanie: `customer` i `product_order` są czytelne po
  // clients.read (batch3), a fence po produktach idzie przez
  // jwt_tenant_product_ids(), które nie wymaga żadnego products.*. Zakres ALL,
  // bo lista nie jest scope'owana po trenerze — patrz `productsCustomers`.
  const canSeeCustomers = usePermissionRequirement(dashboardPermissionRequirements.productsCustomers);

  const tabs = useMemo(() => {
    return [
      {
        key: "products",
        label: t("productsList"),
        children: <ProductsList />,
      },
      {
        key: "productsTransactions",
        label: t("productTransactions.title"),
        children: <ProductsTransactions />,
      },
      ...(canSeeCustomers
        ? [
            {
              key: "customers",
              label: t("customersTab.title"),
              children: <ProductsCustomers />,
            },
          ]
        : []),
      {
        key: "discounts",
        label: t("discounts.tabTitle"),
        children: <DiscountsList />,
      },
      {
        // Windykacja rat (TRE-2168): sprawy per klientka, niezależnie od produktu.
        key: "collections",
        label: "Należności",
        children: <CollectionsDashboard />,
      },
      {
        key: "settings",
        label: t("settings"),
        children: <ProductsSettings />,
      },
    ];
  }, [t, canSeeCustomers]);

  return (
    <Row>
      <Col xs={24}>
        <Card title={t("products")}>
          <Tabs
            type="card"
            hideAdd
            activeKey={searchParams.get("tab") || "products"}
            items={tabs}
            onChange={(activeKey) => setSearchParams((prev) => ({ ...prev, tab: activeKey }))}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default Products;
